import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import type { TokenMetadata } from '@/utils/0x.types';
import { routes } from '@/utils/routes';

import { getFeeOnTransferFromPair } from '../../MarketMode/utils';

const { trigger } = popover();

const tooltipTrigger = (
  <button className={trigger()}>
    <InfoIconOutline className="justify-center text-token-gray-1600" />
  </button>
);

const feeOnTransferTooltipContent =
  'Some tokens have a buy/sell tax and will take a percentage of a token amount when any transfer of the token is performed. Matcha does not receive any of these fees.';

interface FeeOnTransferAlertProps {
  buyTokenSymbol: string;
  sellTokenSymbol: string;
  tokenMetadata: TokenMetadata | undefined;
}

const formatFeeText = (fee: { feeOnTransferToken: string; feeOnTransferValue: number }) => {
  const feePercentage = `${fee.feeOnTransferValue / 100}%`;

  return `${feePercentage} ${fee.feeOnTransferToken}`;
};

export const FeeOnTransferAlert = ({
  buyTokenSymbol,
  sellTokenSymbol,
  tokenMetadata,
}: FeeOnTransferAlertProps) => {
  const { buyTokenFee, sellTokenFee } = getFeeOnTransferFromPair({
    tokenMetadata,
    buyTokenSymbol,
    sellTokenSymbol,
  });

  const isSellTax = !!sellTokenFee;
  const isBuyTax = !!buyTokenFee;
  const isBothTax = isSellTax && isBuyTax;

  const buyTokenFeeText = buyTokenFee && formatFeeText(buyTokenFee);
  const sellTokenFeeText = sellTokenFee && formatFeeText(sellTokenFee);

  const leftText = (
    <>
      {isBothTax
        ? `${sellTokenFeeText} sell tax and ${buyTokenFeeText} buy tax detected`
        : isBuyTax
          ? `${buyTokenFeeText} buy tax detected`
          : isSellTax
            ? `${sellTokenFeeText} sell tax detected`
            : ''}
      <span className="text-token-gray-700">&nbsp;·&nbsp;</span>
      <ExternalLink href={routes.HELP_FEE_ON_TRANSFER} className="text-token-gray-800 underline">
        Learn more
      </ExternalLink>
    </>
  );

  return (
    <div
      className={fontStyles({
        variant: 'LABEL_MD',
        class: [
          'flex',
          'items-center',
          'py-2.5',
          'px-3',
          'mt-3',
          'bg-token-gray-200',
          'rounded-[88px]',
        ],
      })}
    >
      <MatchaTooltip delayDuration={0}>
        <MatchaTooltipTrigger>{tooltipTrigger}</MatchaTooltipTrigger>
        <MatchaTooltipContent>{feeOnTransferTooltipContent}</MatchaTooltipContent>
        <MatchaTooltipPopover sideOffset={0} />
        <MatchaTooltipBottomSheet
          header={`${
            buyTokenFeeText && sellTokenFeeText
              ? `${sellTokenFeeText} and ${buyTokenFeeText}`
              : buyTokenFeeText || sellTokenFeeText
          } buy/sell tax detected`}
        />
      </MatchaTooltip>
      <span className="mx-3 text-token-gray-1600">{leftText}</span>
    </div>
  );
};
